import React, { useState, useEffect, useContext, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DashboardLayout, PageHeader } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert.js';
import Button from '../common/Button.js';
import SelectField from '../common/SelectField.js';
import companySettingsService from '../../services/companySettingsService.js';
import DestinationHubMultiSelect from '../location-hub/DestinationHubMultiSelect.js';
import { formInputClass, formLabelClass, listScopeTabClass } from '../common/uiClasses.js';
import { Settings, Save, IndianRupee, Percent, Truck, FileSpreadsheet, Scale, Shuffle, Layers, Wallet, Lock, Calendar, Clock, FileDigit, ShieldCheck, CheckSquare, Square, RotateCcw, Users, X } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext.js';
import { modLabel } from '../../utils/keyboard.js';
import { sumEnabledPercentCharges } from '../../utils/cargoParcelUtils.js';
import {
  DEFAULT_LABOUR_CHARGE_POLICY,
  LABOUR_CAPTURE_OPTIONS,
  LABOUR_WORKFLOWS,
  labourWorkflowId,
  normalizeLabourChargePolicy,
} from '../../utils/labourChargePolicy.js';
import useCompanySettingsHotkeys from '../../hooks/useCompanySettingsHotkeys.js';
import DocumentIdFormatBuilder from './DocumentIdFormatBuilder.js';
import HubSequenceSettings from './HubSequenceSettings.js';
import { LR_SHIPMENT_ID_FORMAT, ensureMonthLetter } from '../../utils/shipmentIdFormat.js';
import userService from '../../services/UserService.js';
import {
  ALWAYS_ALL_REPORT_ROLES,
  ALL_REPORT_TYPES,
  REPORT_TYPE_OPTIONS,
  defaultReportTypesForRole,
} from '../report/reportConfig.js';
import {
  CONFIGURABLE_ROLES,
  ROLE_MODULE_LABELS,
  ROLE_MODULE_ORDER,
  mergeRoleModuleAccess,
} from '../../utils/roleModuleAccess.js';

const DEFAULT_RCM = { enabled: false, igst: 5, cgst: 2.5, sgst: 2.5 };
const DEFAULT_DEMURRAGE = {
  enabled: false,
  free_hours: 24,
  clock_from: 'received',
  slabs: [
    { max_hours: 4, amount: 0 },
    { max_hours: 8, amount: 100 },
    { max_hours: '', amount: 200 },
  ],
};
const DEFAULT_SHIPMENT_ID_FORMAT = '{sender}-{seq}-{fy}';
const DEFAULT_ML_SHEET_ID_FORMAT = '{sender}-{seq}-{fy}';
const DEFAULT_CHARGE_ITEM = { enabled: false, amount: 0, mode: 'fixed' };
const DEFAULT_SHIPMENT_CHARGES = {
  insurance: { ...DEFAULT_CHARGE_ITEM },
  docket: { ...DEFAULT_CHARGE_ITEM },
  handling: { ...DEFAULT_CHARGE_ITEM },
  door_pickup: { ...DEFAULT_CHARGE_ITEM },
  door_delivery: { ...DEFAULT_CHARGE_ITEM },
  loading: { ...DEFAULT_CHARGE_ITEM },
  unloading: { ...DEFAULT_CHARGE_ITEM },
  toll: { ...DEFAULT_CHARGE_ITEM },
  fuel_surcharge: { ...DEFAULT_CHARGE_ITEM },
  packing: { ...DEFAULT_CHARGE_ITEM },
};

const DEFAULT_EXPORT_COLUMNS = {
  quantity: true,
  freight: true,
  insurance: false,
  rate_per_kg: false,
  loading: false,
  unloading: false,
  toll: false,
  fuel_surcharge: false,
  packing: false,
  docket: true,
  other: true,
  paid: true,
  balance: true,
  mode: true,
  total_gst: true,
  gst_payer: true,
};

/** Optional columns after ROUTE — same order on PDF + Excel export */
const OPTIONAL_EXPORT_FIELDS = [
  { key: 'quantity', label: 'Quantity' },
  { key: 'freight', label: 'Freight' },
  { key: 'insurance', label: 'Insurance' },
  { key: 'rate_per_kg', label: 'Rate / KG' },
  { key: 'loading', label: 'Loading' },
  { key: 'unloading', label: 'Unloading' },
  { key: 'toll', label: 'Toll' },
  { key: 'fuel_surcharge', label: 'Fuel Surcharge' },
  { key: 'packing', label: 'Packing' },
  { key: 'docket', label: 'Docket' },
  { key: 'other', label: 'Other' },
  { key: 'paid', label: 'Paid' },
  { key: 'balance', label: 'Balance' },
  { key: 'mode', label: 'Mode' },
];

const RCM_EXPORT_FIELDS = [
  { key: 'total_gst', label: 'Total GST' },
  { key: 'gst_payer', label: 'GST Payer' },
];

const CHARGE_FIELDS = [
  { key: 'insurance', label: 'Insurance' },
  { key: 'docket', label: 'Docket' },
  { key: 'handling', label: 'Handling' },
  { key: 'door_pickup', label: 'Door Pickup' },
  { key: 'door_delivery', label: 'Door Delivery' },
  { key: 'loading', label: 'Loading' },
  { key: 'unloading', label: 'Unloading' },
  { key: 'toll', label: 'Toll' },
  { key: 'fuel_surcharge', label: 'Fuel Surcharge' },
  { key: 'packing', label: 'Packing' },
];

const ML_TABLE_COLUMNS = [
  { key: 'sr', label: 'SR No.' },
  { key: 'shipment_id', label: 'Shipment ID' },
  { key: 'destination', label: 'Destination' },
  { key: 'consignor', label: 'Consignor' },
  { key: 'consignor_no', label: 'Consignor No.' },
  { key: 'consignee', label: 'Consignee' },
  { key: 'consignee_no', label: 'Consignee No.' },
  { key: 'qty', label: 'Qty' },
  { key: 'payment_mode', label: 'Payment Mode' },
  { key: 'to_pay', label: 'To Pay', amount: true },
  { key: 'paid', label: 'Paid', amount: true },
  { key: 'balance', label: 'Balance', amount: true },
  { key: 'remark', label: 'Remark' },
];

const ML_SUMMARY_COLUMNS = [
  { key: 'freight', label: 'Freight' },
  ...CHARGE_FIELDS,
  { key: 'st', label: 'ST' },
];

const CHARGE_KEY_SET = new Set(CHARGE_FIELDS.map(({ key }) => key));

const mergeMlPdfCharges = (saved, chargeDefaults) => {
  const next = {};
  [...ML_TABLE_COLUMNS, ...ML_SUMMARY_COLUMNS].forEach(({ key }) => {
    if (saved && typeof saved === 'object' && Object.prototype.hasOwnProperty.call(saved, key)) {
      next[key] = !!saved[key];
    } else if (CHARGE_KEY_SET.has(key)) {
      next[key] = !!chargeDefaults?.[key]?.enabled;
    } else {
      next[key] = true;
    }
  });
  return next;
};

const settingsGridClass = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 min-w-0';

const SETTINGS_CATEGORIES = [
  { id: 'finance', label: 'Finance & tax', description: 'GST, default booking charges, and settlement or remittance rules.' },
  { id: 'booking', label: 'Booking & operations', description: 'How new shipments are booked.' },
  { id: 'manifest', label: 'Manifest & cash', description: 'ML creation and cash remittance rules.' },
  { id: 'documents', label: 'Documents & reports', description: 'IDs, ML sheet layout, export columns, and access.' },
  { id: 'hr', label: 'HR', description: 'Salary cycle for attendance.' },
];

const faintUserChipClass =
  'inline-flex items-center max-w-[9rem] truncate rounded-md bg-slate-50/70 px-1.5 py-0.5 text-[10px] font-medium text-black border border-gray-100/80';

function reportUserOptionContent(user) {
  if (!user) return 'Select user';
  const name = user.name || user.username || user.user_id || 'User';
  const role = user.role_name || user.role;
  const hub = user.location_hub_name || user.hub_name || user.location;
  const chips = [role, user.username !== name ? user.username : null, hub, user.user_id]
    .filter(Boolean);
  return (
    <span className="flex items-center gap-1.5 min-w-0">
      <span className="font-semibold text-secondary truncate">{name}</span>
      {chips.map((chip, idx) => (
        <span key={`${user.user_id || name}-${idx}`} className={faintUserChipClass}>{chip}</span>
      ))}
    </span>
  );
}

const SettingsModalContext = React.createContext({
  saving: false,
  onSave: () => {},
  onCancel: () => {},
});

const ordinalDay = (day) => {
  const n = Number(day) || 1;
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

const CompanySettings = () => {
  const { user, patchUser } = useContext(AuthContext);
  const isInternalCompany = user?.network_mode !== 'EXTERNAL';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rcm, setRcm] = useState(DEFAULT_RCM);
  const [demurrage, setDemurrage] = useState(DEFAULT_DEMURRAGE);
  const [shipmentIdFormat, setShipmentIdFormat] = useState(DEFAULT_SHIPMENT_ID_FORMAT);
  const [mlSheetIdFormat, setMlSheetIdFormat] = useState(DEFAULT_ML_SHEET_ID_FORMAT);
  const [labourPolicy, setLabourPolicy] = useState(DEFAULT_LABOUR_CHARGE_POLICY);
  const [isCargoShipment, setIsCargoShipment] = useState(false);
  const [busFreightByWeight, setBusFreightByWeight] = useState(false);
  const [isCrossingEnabled, setIsCrossingEnabled] = useState(true);
  const [isAutoMlEnabled, setIsAutoMlEnabled] = useState(true);
  const [requirePaidSettlementBeforeMl, setRequirePaidSettlementBeforeMl] = useState(true);
  const [lockMlShipmentDetailsUntilRemit, setLockMlShipmentDetailsUntilRemit] = useState(true);
  const [mlSheetPdfOrientation, setMlSheetPdfOrientation] = useState('landscape');
  const [mlSheetSummaryMode, setMlSheetSummaryMode] = useState('financial');
  const [mlSheetPdfCharges, setMlSheetPdfCharges] = useState(() => (
    mergeMlPdfCharges(null, DEFAULT_SHIPMENT_CHARGES)
  ));
  const [labourReserveEnabled, setLabourReserveEnabled] = useState(false);
  const [lrBranchCodeRequired, setLrBranchCodeRequired] = useState(false);
  const [sequenceMode, setSequenceMode] = useState('shared');
  const [sequenceResetsMonthly, setSequenceResetsMonthly] = useState(false);
  const [sequenceStarts, setSequenceStarts] = useState([]);
  const [mlCommissionRemitMode, setMlCommissionRemitMode] = useState('labour_keep_separate');
  const [collectHubIds, setCollectHubIds] = useState([]);
  const [adminTransferHubIds, setAdminTransferHubIds] = useState([]);
  const [settleHubOptions, setSettleHubOptions] = useState([]);
  const [ownHubSettleCollectedOnly, setOwnHubSettleCollectedOnly] = useState(true);
  const [parcelTypes, setParcelTypes] = useState([]);
  const [parcelDraft, setParcelDraft] = useState('');
  const [salaryCycleStartDay, setSalaryCycleStartDay] = useState(1);
  const [attendanceEnabled, setAttendanceEnabled] = useState(false);
  const [charges, setCharges] = useState(DEFAULT_SHIPMENT_CHARGES);
  const [exportColumns, setExportColumns] = useState(DEFAULT_EXPORT_COLUMNS);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [reportPermissions, setReportPermissions] = useState({});
  const [reportPermUserId, setReportPermUserId] = useState('');
  const [customizedReportUsers, setCustomizedReportUsers] = useState(() => new Set());
  const [roleModuleAccess, setRoleModuleAccess] = useState(() => mergeRoleModuleAccess(user?.company_features, {}, {
    crossingEnabled: user?.is_crossing_enabled !== false,
    networkMode: user?.network_mode,
  }));
  const [roleModuleRole, setRoleModuleRole] = useState('Accountant');
  /** Accordion: only one settings section open at a time */
  const [openSection, setOpenSection] = useState(null);
  const settingsDraftRef = useRef(null);
  const [activeCategory, setActiveCategory] = useState('finance');

  const visibleCategories = SETTINGS_CATEGORIES.filter((cat) => cat.id !== 'hr' || attendanceEnabled);
  const currentCategory = visibleCategories.some((cat) => cat.id === activeCategory)
    ? activeCategory
    : visibleCategories[0]?.id;
  const activeCategoryMeta = visibleCategories.find((cat) => cat.id === currentCategory);

  const categorySettingCount = (id) => {
    switch (id) {
      case 'finance':
        return 2 + (isInternalCompany ? 2 : 0);
      case 'booking':
        return 3 + (!isCargoShipment ? 1 : 0) + (isInternalCompany ? 1 : 0);
      case 'manifest':
        return 3 + (isInternalCompany ? 3 : 0);
      case 'documents':
        return 5;
      case 'hr':
        return 1;
      default:
        return 0;
    }
  };

  const captureSettingsDraft = () => ({
    rcm,
    demurrage,
    shipmentIdFormat,
    mlSheetIdFormat,
    labourPolicy,
    isCargoShipment,
    busFreightByWeight,
    isCrossingEnabled,
    isAutoMlEnabled,
    requirePaidSettlementBeforeMl,
    lockMlShipmentDetailsUntilRemit,
    mlSheetPdfOrientation,
    mlSheetSummaryMode,
    mlSheetPdfCharges,
    labourReserveEnabled,
    lrBranchCodeRequired,
    sequenceMode,
    sequenceResetsMonthly,
    sequenceStarts,
    mlCommissionRemitMode,
    collectHubIds,
    adminTransferHubIds,
    ownHubSettleCollectedOnly,
    parcelTypes,
    parcelDraft,
    salaryCycleStartDay,
    charges,
    exportColumns,
    reportPermissions,
    reportPermUserId,
    customizedReportUsers: Array.from(customizedReportUsers),
    roleModuleAccess,
    roleModuleRole,
  });

  const restoreSettingsDraft = (draft) => {
    if (!draft) return;
    setRcm(draft.rcm);
    setDemurrage(draft.demurrage);
    setShipmentIdFormat(draft.shipmentIdFormat);
    setMlSheetIdFormat(draft.mlSheetIdFormat);
    setLabourPolicy(draft.labourPolicy);
    setIsCargoShipment(draft.isCargoShipment);
    setBusFreightByWeight(draft.busFreightByWeight);
    setIsCrossingEnabled(draft.isCrossingEnabled);
    setIsAutoMlEnabled(draft.isAutoMlEnabled);
    setRequirePaidSettlementBeforeMl(draft.requirePaidSettlementBeforeMl);
    setLockMlShipmentDetailsUntilRemit(draft.lockMlShipmentDetailsUntilRemit);
    setMlSheetPdfOrientation(draft.mlSheetPdfOrientation);
    setMlSheetSummaryMode(draft.mlSheetSummaryMode);
    setMlSheetPdfCharges(draft.mlSheetPdfCharges);
    setLabourReserveEnabled(draft.labourReserveEnabled);
    setLrBranchCodeRequired(draft.lrBranchCodeRequired);
    setSequenceMode(draft.sequenceMode);
    setSequenceResetsMonthly(draft.sequenceResetsMonthly);
    setSequenceStarts(draft.sequenceStarts);
    setMlCommissionRemitMode(draft.mlCommissionRemitMode);
    setCollectHubIds(draft.collectHubIds);
    setAdminTransferHubIds(draft.adminTransferHubIds);
    setOwnHubSettleCollectedOnly(draft.ownHubSettleCollectedOnly);
    setParcelTypes(draft.parcelTypes);
    setParcelDraft(draft.parcelDraft);
    setSalaryCycleStartDay(draft.salaryCycleStartDay);
    setCharges(draft.charges);
    setExportColumns(draft.exportColumns);
    setReportPermissions(draft.reportPermissions);
    setReportPermUserId(draft.reportPermUserId);
    setCustomizedReportUsers(new Set(draft.customizedReportUsers || []));
    setRoleModuleAccess(draft.roleModuleAccess);
    setRoleModuleRole(draft.roleModuleRole);
  };

  const openSettingsModal = (key) => {
    settingsDraftRef.current = captureSettingsDraft();
    setOpenSection(key);
  };
  const toggleSection = openSettingsModal;

  const closeSettingsModal = (restore = false) => {
    if (restore) restoreSettingsDraft(settingsDraftRef.current);
    settingsDraftRef.current = null;
    setOpenSection(null);
  };

  const saveSettingsModal = async () => {
    const ok = await handleSave();
    if (ok) {
      settingsDraftRef.current = null;
      setOpenSection(null);
    }
  };

  const handleCategoryChange = (id) => {
    if (openSection) closeSettingsModal(true);
    setActiveCategory(id);
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const mergeChargeDefaults = (raw) => {
    const merged = { ...DEFAULT_SHIPMENT_CHARGES };
    if (!raw || typeof raw !== 'object') return merged;
    Object.keys(merged).forEach((key) => {
      merged[key] = { ...DEFAULT_SHIPMENT_CHARGES[key], ...(raw[key] || {}) };
    });
    // Legacy fallback: old shipment_insurance_charge alone
    if (!raw.insurance && raw.enabled !== undefined) {
      merged.insurance = {
        enabled: !!raw.enabled,
        amount: raw.amount ?? 0,
        mode: raw.mode === 'percent' ? 'percent' : 'fixed',
      };
    }
    Object.keys(merged).forEach((key) => {
      merged[key] = {
        ...merged[key],
        mode: merged[key]?.mode === 'percent' ? 'percent' : 'fixed',
      };
    });
    return merged;
  };

  const mergeExportColumns = (raw, rcmEnabled) => {
    const merged = { ...DEFAULT_EXPORT_COLUMNS, ...(raw || {}) };
    Object.keys(DEFAULT_EXPORT_COLUMNS).forEach((key) => {
      merged[key] = !!merged[key];
    });
    if (rcmEnabled) {
      RCM_EXPORT_FIELDS.forEach(({ key }) => {
        merged[key] = true;
      });
    } else {
      RCM_EXPORT_FIELDS.forEach(({ key }) => {
        merged[key] = false;
      });
    }
    return merged;
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await companySettingsService.getSettings();
      const nextRcm = { ...DEFAULT_RCM, ...(res?.data?.rcm || {}) };
      setRcm(nextRcm);
      const nextDemurrage = { ...DEFAULT_DEMURRAGE, ...(res?.data?.demurrage || {}) };
      nextDemurrage.enabled = !!nextDemurrage.enabled;
      const freeH = parseFloat(nextDemurrage.free_hours);
      nextDemurrage.free_hours = Number.isFinite(freeH) ? freeH : DEFAULT_DEMURRAGE.free_hours;
      nextDemurrage.clock_from = nextDemurrage.clock_from === 'booked' ? 'booked' : 'received';
      nextDemurrage.slabs = Array.isArray(nextDemurrage.slabs) && nextDemurrage.slabs.length
        ? nextDemurrage.slabs.map((s) => ({
            max_hours: s.max_hours == null || s.max_hours === undefined ? '' : s.max_hours,
            amount: s.amount ?? 0,
          }))
        : DEFAULT_DEMURRAGE.slabs.map((s) => ({ ...s }));
      setDemurrage(nextDemurrage);
      setShipmentIdFormat(res?.data?.shipment_id_format || DEFAULT_SHIPMENT_ID_FORMAT);
      setMlSheetIdFormat(res?.data?.ml_sheet_id_format || DEFAULT_ML_SHEET_ID_FORMAT);
      setLabourPolicy(normalizeLabourChargePolicy(res?.data?.labour_charge_policy));
      const nextBusFreight = !!res?.data?.bus_freight_by_weight;
      const nextCargo = nextBusFreight ? false : !!res?.data?.is_cargo_shipment;
      setBusFreightByWeight(nextCargo ? false : nextBusFreight);
      setIsCargoShipment(nextCargo);
      setIsCrossingEnabled(res?.data?.is_crossing_enabled !== false);
      setIsAutoMlEnabled(res?.data?.is_auto_ml_enabled !== false);
      setRequirePaidSettlementBeforeMl(res?.data?.require_paid_settlement_before_ml !== false);
      setLockMlShipmentDetailsUntilRemit(res?.data?.lock_ml_shipment_details_until_remit !== false);
      setMlSheetPdfOrientation(
        res?.data?.ml_sheet_pdf_orientation === 'portrait' ? 'portrait' : 'landscape',
      );
      setMlSheetSummaryMode(
        res?.data?.ml_sheet_summary_mode === 'operational' ? 'operational' : 'financial',
      );
      const loadedCharges = mergeChargeDefaults(
        res?.data?.shipment_charge_defaults ||
        {
          ...(res?.data?.cargo_charge_defaults || {}),
          insurance: res?.data?.shipment_insurance_charge,
        },
      );
      setMlSheetPdfCharges(mergeMlPdfCharges(res?.data?.ml_sheet_pdf_charges, loadedCharges));
      setLabourReserveEnabled(!!res?.data?.labour_reserve_enabled);
      setLrBranchCodeRequired(!!res?.data?.lr_branch_code_required);
      setSequenceMode(res?.data?.shipment_sequence_mode || 'shared');
      setSequenceResetsMonthly(!!res?.data?.shipment_sequence_resets_monthly);
      setSequenceStarts(Array.isArray(res?.data?.hub_sequence_starts) ? res.data.hub_sequence_starts : []);
      setMlCommissionRemitMode('labour_keep_separate');
      const savedCollect = Array.isArray(res?.data?.settlement_collect_hub_ids)
        && res.data.settlement_collect_hub_ids.length
        ? res.data.settlement_collect_hub_ids
        : (res?.data?.settlement_collect_hub_id != null ? [res.data.settlement_collect_hub_id] : []);
      setCollectHubIds(savedCollect.map(String));
      const savedAdmin = Array.isArray(res?.data?.settlement_admin_transfer_hub_ids)
        && res.data.settlement_admin_transfer_hub_ids.length
        ? res.data.settlement_admin_transfer_hub_ids
        : (res?.data?.settlement_admin_transfer_hub_id != null
          ? [res.data.settlement_admin_transfer_hub_id]
          : []);
      setAdminTransferHubIds(savedAdmin.map(String));
      try {
        const { locationHubService } = await import('../../services/locationHubService.js');
        const hubRes = await locationHubService.getHubsForSelect();
        const hubs = hubRes?.data?.hubs || hubRes?.data || [];
        setSettleHubOptions(Array.isArray(hubs) ? hubs : []);
      } catch {
        setSettleHubOptions([]);
      }
      setOwnHubSettleCollectedOnly(res?.data?.own_hub_settle_collected_only !== false);
      try {
        const parcels = await companySettingsService.listParcelTypes({ include_inactive: true });
        setParcelTypes(parcels?.data || []);
      } catch {
        setParcelTypes([]);
      }
      const cycleDay = parseInt(res?.data?.salary_cycle_start_day, 10);
      setSalaryCycleStartDay(Number.isFinite(cycleDay) ? Math.min(28, Math.max(1, cycleDay)) : 1);
      setAttendanceEnabled(!!res?.data?.attendance_enabled);
      setRoleModuleAccess(mergeRoleModuleAccess(
        res?.data?.company_features || user?.company_features,
        res?.data?.role_module_access,
        {
          crossingEnabled: res?.data?.is_crossing_enabled !== false,
          networkMode: user?.network_mode,
        },
      ));
      setCharges(loadedCharges);
      setExportColumns(
        mergeExportColumns(res?.data?.shipment_export_columns, !!nextRcm.enabled),
      );
      const rawPerms = res?.data?.report_permissions;
      const loadedPerms = rawPerms && !Array.isArray(rawPerms) && typeof rawPerms === 'object'
        ? rawPerms
        : {};
      setReportPermissions(loadedPerms);
      setCustomizedReportUsers(new Set(Object.keys(loadedPerms)));
      try {
        const usersRes = await userService.getAll(1, 500, { all: 'true' });
        const list = Array.isArray(usersRes?.data)
          ? usersRes.data
          : (Array.isArray(usersRes) ? usersRes : []);
        const users = list
          .filter((row) => (row.role_name || row.role) !== 'System Admin')
          .sort((a, b) => String(a.name || a.username || '').localeCompare(String(b.name || b.username || '')));
        setCompanyUsers(users);
        setReportPermUserId((prev) => prev || users[0]?.user_id || '');
      } catch {
        setCompanyUsers([]);
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load company settings');
    } finally {
      setLoading(false);
    }
  };

  const handleRateChange = (key, value) => {
    setRcm((prev) => ({ ...prev, [key]: value }));
  };

  const handleChargeChange = (key, field, value) => {
    setCharges((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: value },
    }));
    if (field === 'enabled' && value) {
      setMlSheetPdfCharges((prev) => ({ ...prev, [key]: true }));
    }
  };

  const handleExportColumnChange = (key, checked) => {
    if (rcm.enabled && RCM_EXPORT_FIELDS.some((f) => f.key === key)) {
      return;
    }
    setExportColumns((prev) => ({ ...prev, [key]: checked }));
  };

  const selectedReportUser = companyUsers.find((u) => String(u.user_id) === String(reportPermUserId));
  const selectedReportRole = selectedReportUser?.role_name || selectedReportUser?.role;
  const selectedReportLocked = ALWAYS_ALL_REPORT_ROLES.has(selectedReportRole);
  const selectedReportTypes = (() => {
    if (!reportPermUserId) return [];
    if (selectedReportLocked) return [...ALL_REPORT_TYPES];
    if (customizedReportUsers.has(String(reportPermUserId)) && Array.isArray(reportPermissions[reportPermUserId])) {
      return reportPermissions[reportPermUserId];
    }
    return defaultReportTypesForRole(selectedReportRole);
  })();

  const markReportUserCustomized = (userId, types) => {
    const uid = String(userId);
    setReportPermissions((prev) => ({ ...prev, [uid]: types }));
    setCustomizedReportUsers((prev) => {
      const next = new Set(prev);
      next.add(uid);
      return next;
    });
  };

  const toggleReportTypeForUser = (reportType, checked) => {
    if (!reportPermUserId || selectedReportLocked) return;
    const current = new Set(selectedReportTypes);
    if (checked) current.add(reportType);
    else current.delete(reportType);
    markReportUserCustomized(reportPermUserId, [...current]);
  };

  const toggleAllReportsForUser = (checked) => {
    if (!reportPermUserId || selectedReportLocked) return;
    markReportUserCustomized(
      reportPermUserId,
      checked ? [...ALL_REPORT_TYPES] : [],
    );
  };

  const resetReportUserToDefaults = () => {
    if (!reportPermUserId || selectedReportLocked) return;
    const uid = String(reportPermUserId);
    setReportPermissions((prev) => {
      const next = { ...prev };
      delete next[uid];
      return next;
    });
    setCustomizedReportUsers((prev) => {
      const next = new Set(prev);
      next.delete(uid);
      return next;
    });
  };

  const handleRcmToggle = (checked) => {
    setRcm((prev) => ({ ...prev, enabled: checked }));
    setExportColumns((prev) => mergeExportColumns(prev, checked));
  };

  /** Bus freight is for bus bookings only — mutually exclusive with cargo mode */
  const handleBusFreightToggle = (checked) => {
    setBusFreightByWeight(checked);
    if (checked) {
      setIsCargoShipment(false);
    }
  };

  const handleCargoToggle = (checked) => {
    setIsCargoShipment(checked);
    if (checked) {
      setBusFreightByWeight(false);
    }
  };

  const handleSave = async () => {
    const chargePayload = {};
    let extraPercentSum = 0;
    Object.keys(DEFAULT_SHIPMENT_CHARGES).forEach((key) => {
      const mode = charges[key]?.mode === 'percent' ? 'percent' : 'fixed';
      chargePayload[key] = {
        enabled: !!charges[key]?.enabled,
        amount: parseFloat(charges[key]?.amount) || 0,
        mode,
      };
      if (key !== 'insurance' && charges[key]?.enabled && mode === 'percent') {
        extraPercentSum += chargePayload[key].amount;
      }
    });
    if (extraPercentSum > 100) {
      Alert.error('Enabled extra charges in % mode cannot add up to more than 100% of article charge.');
      return;
    }

    const exportPayload = { ...DEFAULT_EXPORT_COLUMNS };
    Object.keys(DEFAULT_EXPORT_COLUMNS).forEach((key) => {
      exportPayload[key] = !!exportColumns[key];
    });
    if (rcm.enabled) {
      RCM_EXPORT_FIELDS.forEach(({ key }) => {
        exportPayload[key] = true;
      });
    }

    const payload = {
      rcm: {
        enabled: !!rcm.enabled,
        igst: parseFloat(rcm.igst) || 0,
        cgst: parseFloat(rcm.cgst) || 0,
        sgst: parseFloat(rcm.sgst) || 0,
      },
      demurrage: {
        enabled: !!demurrage.enabled,
        free_hours: parseFloat(demurrage.free_hours) || 0,
        clock_from: demurrage.clock_from === 'booked' ? 'booked' : 'received',
        slabs: (demurrage.slabs || []).map((s) => ({
          max_hours: s.max_hours === '' || s.max_hours == null ? null : parseFloat(s.max_hours),
          amount: parseFloat(s.amount) || 0,
        })),
      },
      // Bus freight by weight cannot run with cargo mode
      is_cargo_shipment: !!isCargoShipment,
      bus_freight_by_weight: isCargoShipment ? false : !!busFreightByWeight,
      shipment_charge_defaults: chargePayload,
      shipment_export_columns: exportPayload,
      ml_sheet_pdf_orientation: mlSheetPdfOrientation === 'portrait' ? 'portrait' : 'landscape',
      ml_sheet_summary_mode: mlSheetSummaryMode === 'operational' ? 'operational' : 'financial',
      ml_sheet_pdf_charges: mergeMlPdfCharges(mlSheetPdfCharges, charges),
      labour_reserve_enabled: !!labourReserveEnabled,
      lr_branch_code_required: !!lrBranchCodeRequired,
      ml_commission_remit_mode: 'labour_keep_separate',
      settlement_collect_hub_ids: (collectHubIds || []).map(Number),
      settlement_collect_hub_id: (collectHubIds || [])[0] ? Number(collectHubIds[0]) : null,
      settlement_admin_transfer_hub_ids: (adminTransferHubIds || []).map(Number),
      settlement_admin_transfer_hub_id: (adminTransferHubIds || [])[0]
        ? Number(adminTransferHubIds[0])
        : null,
      own_hub_settle_collected_only: !!ownHubSettleCollectedOnly,
      require_paid_settlement_before_ml: !!requirePaidSettlementBeforeMl,
      lock_ml_shipment_details_until_remit: !!lockMlShipmentDetailsUntilRemit,
      shipment_id_format: (
        sequenceMode !== 'shared' && sequenceResetsMonthly
          ? ensureMonthLetter(shipmentIdFormat || DEFAULT_SHIPMENT_ID_FORMAT)
          : (shipmentIdFormat || DEFAULT_SHIPMENT_ID_FORMAT)
      ),
      ml_sheet_id_format: (
        sequenceMode !== 'shared' && sequenceResetsMonthly
          ? ensureMonthLetter(mlSheetIdFormat || DEFAULT_ML_SHEET_ID_FORMAT)
          : (mlSheetIdFormat || DEFAULT_ML_SHEET_ID_FORMAT)
      ),
      shipment_sequence_mode: sequenceMode || 'shared',
      shipment_sequence_resets_monthly: !!sequenceResetsMonthly,
      hub_sequence_starts: (sequenceStarts || []).map((row) => ({
        location_hub_id: row.location_hub_id,
        shipment_start: Math.max(0, parseInt(row.shipment_start, 10) || 0),
        ml_start: Math.max(0, parseInt(row.ml_start, 10) || 0),
      })),
      labour_charge_policy: labourPolicy,
    };
    if (attendanceEnabled) {
      payload.salary_cycle_start_day = salaryCycleStartDay;
    }
    if (isInternalCompany) {
      payload.is_crossing_enabled = !!isCrossingEnabled;
      payload.is_auto_ml_enabled = !!isAutoMlEnabled;
    }
    const permPayload = {};
    customizedReportUsers.forEach((uid) => {
      const person = companyUsers.find((row) => String(row.user_id) === String(uid));
      const role = person?.role_name || person?.role;
      if (ALWAYS_ALL_REPORT_ROLES.has(role)) return;
      permPayload[uid] = Array.isArray(reportPermissions[uid])
        ? reportPermissions[uid]
        : [];
    });
    payload.report_permissions = permPayload;
    payload.role_module_access = roleModuleAccess;

    if (payload.rcm.igst < 0 || payload.rcm.cgst < 0 || payload.rcm.sgst < 0) {
      Alert.error('GST rates cannot be negative');
      return false;
    }
    if (Object.values(chargePayload).some((c) => c.amount < 0)) {
      Alert.error('Charge amounts cannot be negative');
      return false;
    }

    try {
      setSaving(true);
      const res = await companySettingsService.updateSettings(payload);
      const nextRcm = { ...DEFAULT_RCM, ...(res?.data?.rcm || payload.rcm) };
      setRcm(nextRcm);
      const savedDem = { ...DEFAULT_DEMURRAGE, ...(res?.data?.demurrage || payload.demurrage) };
      savedDem.enabled = !!savedDem.enabled;
      savedDem.clock_from = savedDem.clock_from === 'booked' ? 'booked' : 'received';
      savedDem.slabs = Array.isArray(savedDem.slabs) && savedDem.slabs.length
        ? savedDem.slabs.map((s) => ({
            max_hours: s.max_hours == null ? '' : s.max_hours,
            amount: s.amount ?? 0,
          }))
        : DEFAULT_DEMURRAGE.slabs.map((s) => ({ ...s }));
      setDemurrage(savedDem);
      setShipmentIdFormat(res?.data?.shipment_id_format || payload.shipment_id_format);
      setMlSheetIdFormat(res?.data?.ml_sheet_id_format || payload.ml_sheet_id_format);
      setSequenceMode(res?.data?.shipment_sequence_mode || payload.shipment_sequence_mode || 'shared');
      setSequenceResetsMonthly(
        !!(res?.data?.shipment_sequence_resets_monthly ?? payload.shipment_sequence_resets_monthly),
      );
      setSequenceStarts(Array.isArray(res?.data?.hub_sequence_starts)
        ? res.data.hub_sequence_starts
        : payload.hub_sequence_starts);
      setLabourPolicy(normalizeLabourChargePolicy(res?.data?.labour_charge_policy || payload.labour_charge_policy));
      const savedCargo = !!(res?.data?.is_cargo_shipment ?? payload.is_cargo_shipment);
      const savedBusFreight = savedCargo
        ? false
        : !!(res?.data?.bus_freight_by_weight ?? payload.bus_freight_by_weight);
      setBusFreightByWeight(savedBusFreight);
      setIsCargoShipment(savedCargo);
      setIsCrossingEnabled(
        (res?.data?.is_crossing_enabled ?? payload.is_crossing_enabled) !== false,
      );
      setIsAutoMlEnabled(
        (res?.data?.is_auto_ml_enabled ?? payload.is_auto_ml_enabled) !== false,
      );
      setRequirePaidSettlementBeforeMl(
        (res?.data?.require_paid_settlement_before_ml ?? payload.require_paid_settlement_before_ml) !== false,
      );
      setLockMlShipmentDetailsUntilRemit(
        (res?.data?.lock_ml_shipment_details_until_remit ?? payload.lock_ml_shipment_details_until_remit) !== false,
      );
      const savedMlOrientation =
        (res?.data?.ml_sheet_pdf_orientation ?? payload.ml_sheet_pdf_orientation) === 'portrait'
          ? 'portrait'
          : 'landscape';
      setMlSheetPdfOrientation(savedMlOrientation);
      const savedMlSummary =
        (res?.data?.ml_sheet_summary_mode ?? payload.ml_sheet_summary_mode) === 'operational'
          ? 'operational'
          : 'financial';
      setMlSheetSummaryMode(savedMlSummary);
      setMlSheetPdfCharges(mergeMlPdfCharges(
        res?.data?.ml_sheet_pdf_charges ?? payload.ml_sheet_pdf_charges,
        mergeChargeDefaults(res?.data?.shipment_charge_defaults || payload.shipment_charge_defaults),
      ));
      const savedLabourReserve = !!(
        res?.data?.labour_reserve_enabled ?? payload.labour_reserve_enabled
      );
      setLabourReserveEnabled(savedLabourReserve);
      setLrBranchCodeRequired(!!(
        res?.data?.lr_branch_code_required ?? payload.lr_branch_code_required
      ));
      const savedRemitMode = 'labour_keep_separate';
      setMlCommissionRemitMode(savedRemitMode);
      setOwnHubSettleCollectedOnly(
        (res?.data?.own_hub_settle_collected_only ?? payload.own_hub_settle_collected_only) !== false,
      );
      if (payload.report_permissions || res?.data?.report_permissions) {
        const savedPerms = res?.data?.report_permissions || payload.report_permissions || {};
        const nextPerms = savedPerms && !Array.isArray(savedPerms) && typeof savedPerms === 'object'
          ? savedPerms
          : {};
        setReportPermissions(nextPerms);
        setCustomizedReportUsers(new Set(Object.keys(nextPerms)));
      }
      setRoleModuleAccess(mergeRoleModuleAccess(
        res?.data?.company_features || user?.company_features,
        res?.data?.role_module_access ?? payload.role_module_access,
        {
          crossingEnabled: (res?.data?.is_crossing_enabled ?? payload.is_crossing_enabled) !== false,
          networkMode: user?.network_mode,
        },
      ));
      if (typeof patchUser === 'function') {
        patchUser({ role_module_access: res?.data?.role_module_access || payload.role_module_access || {} });
      }
      const savedCycle = parseInt(
        res?.data?.salary_cycle_start_day ?? payload.salary_cycle_start_day,
        10,
      );
      if (Number.isFinite(savedCycle)) {
        setSalaryCycleStartDay(Math.min(28, Math.max(1, savedCycle)));
      }
      setAttendanceEnabled(
        res?.data?.attendance_enabled !== undefined
          ? !!res.data.attendance_enabled
          : attendanceEnabled,
      );
      if (typeof patchUser === 'function' && isInternalCompany) {
        patchUser({
          is_cargo_shipment: savedCargo,
          is_crossing_enabled:
            (res?.data?.is_crossing_enabled ?? payload.is_crossing_enabled) !== false,
          is_auto_ml_enabled:
            (res?.data?.is_auto_ml_enabled ?? payload.is_auto_ml_enabled) !== false,
          require_paid_settlement_before_ml:
            (res?.data?.require_paid_settlement_before_ml ?? payload.require_paid_settlement_before_ml) !== false,
          lock_ml_shipment_details_until_remit:
            (res?.data?.lock_ml_shipment_details_until_remit ?? payload.lock_ml_shipment_details_until_remit) !== false,
          labour_reserve_enabled: savedLabourReserve,
          lr_branch_code_required: !!(
            res?.data?.lr_branch_code_required ?? payload.lr_branch_code_required
          ),
          ml_commission_remit_mode: savedRemitMode,
          own_hub_settle_collected_only:
            (res?.data?.own_hub_settle_collected_only ?? payload.own_hub_settle_collected_only) !== false,
        });
      } else if (typeof patchUser === 'function') {
        patchUser({
          is_cargo_shipment: savedCargo,
          labour_reserve_enabled: savedLabourReserve,
          lr_branch_code_required: !!(
            res?.data?.lr_branch_code_required ?? payload.lr_branch_code_required
          ),
          ml_commission_remit_mode: savedRemitMode,
          own_hub_settle_collected_only:
            (res?.data?.own_hub_settle_collected_only ?? payload.own_hub_settle_collected_only) !== false,
          require_paid_settlement_before_ml:
            (res?.data?.require_paid_settlement_before_ml ?? payload.require_paid_settlement_before_ml) !== false,
          lock_ml_shipment_details_until_remit:
            (res?.data?.lock_ml_shipment_details_until_remit ?? payload.lock_ml_shipment_details_until_remit) !== false,
        });
      }
      setCharges(
        mergeChargeDefaults(res?.data?.shipment_charge_defaults || chargePayload),
      );
      setExportColumns(
        mergeExportColumns(
          res?.data?.shipment_export_columns || exportPayload,
          !!nextRcm.enabled,
        ),
      );
      Alert.success('Settings saved successfully');
      return true;
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to save settings');
      return false;
    } finally {
      setSaving(false);
    }
  };

  useCompanySettingsHotkeys({
    enabled: !loading,
    visibleCategories,
    currentCategory,
    onCategoryChange: handleCategoryChange,
    onSave: saveSettingsModal,
    saving,
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12 sm:py-20">
          <Loader fullScreen={false} size="default" message="Loading settings..." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout fullWidth>
      <div className="w-full min-w-0">
        <PageHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Settings className="w-5 h-5 sm:w-6 sm:h-6 text-secondary shrink-0" />
              Company Settings
            </span>
          }
          subtitle="Open a card to edit it. Save in that window applies the change."
        />

        <div
          className="flex gap-2 overflow-x-auto pb-1 mb-3 sm:mb-4 -mx-1 px-1"
          role="tablist"
          aria-label="Settings categories"
        >
          {visibleCategories.map((cat, idx) => {
            const isActive = currentCategory === cat.id;
            const settingCount = categorySettingCount(cat.id);
            return (
            <button
              key={cat.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-label={`${cat.label}, ${settingCount} setting${settingCount === 1 ? '' : 's'}`}
              aria-keyshortcuts={String(idx + 1)}
              onClick={() => handleCategoryChange(cat.id)}
              className={`${listScopeTabClass(isActive)} inline-flex items-center gap-2 whitespace-nowrap shrink-0`}
            >
              <span>{cat.label}</span>
              <span
                className={`inline-flex items-center justify-center min-w-[1.375rem] h-5 px-1.5 rounded-full text-[11px] font-bold tabular-nums leading-none ${
                  isActive
                    ? 'bg-white text-buttonBg shadow-sm'
                    : 'bg-blue-100 text-blue-700'
                }`}
              >
                {settingCount}
              </span>
            </button>
            );
          })}
        </div>
        {activeCategoryMeta?.description ? (
          <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
            {activeCategoryMeta.description}{' '}
            <span className="text-gray-400">Press ? for shortcuts. {modLabel()}+S saves the open window.</span>
          </p>
        ) : null}

        <SettingsModalContext.Provider
          value={{
            saving,
            onSave: saveSettingsModal,
            onCancel: () => closeSettingsModal(true),
          }}
        >
        <div className={settingsGridClass}>
          {currentCategory === 'finance' && (
            <>
          <SettingCard
            icon={Percent}
            iconTone="bg-sky-100 text-sky-600"
            title="RCM (Reverse Charge Mechanism)"
            description="When enabled, GST on new shipments is auto-derived from GST registration and who pays. Fallback rates apply when nobody is registered."
            enabled={!!rcm.enabled}
            onToggle={handleRcmToggle}
            isOpen={openSection === 'rcm'}
            onToggleOpen={() => toggleSection('rcm')}
          >
            <div
              className={`grid grid-cols-3 gap-2 ${rcm.enabled ? '' : 'opacity-50 pointer-events-none'}`}
            >
              <RateInput
                label="IGST %"
                value={rcm.igst}
                onChange={(v) => handleRateChange('igst', v)}
              />
              <RateInput
                label="CGST %"
                value={rcm.cgst}
                onChange={(v) => handleRateChange('cgst', v)}
              />
              <RateInput
                label="SGST %"
                value={rcm.sgst}
                onChange={(v) => handleRateChange('sgst', v)}
              />
            </div>
            <p className="text-[11px] text-gray-400 mt-2 leading-relaxed">
              Same-state: CGST + SGST. Different-state: IGST.
            </p>
          </SettingCard>

          {isInternalCompany && (
            <SettingCard
              icon={Clock}
              iconTone="bg-rose-100 text-rose-700"
              title="Demurrage (late delivery)"
              description="After the free hours from Received (or Booked), apply a fixed ₹ slab. Delivered is blocked until the extra amount is collected as Cash or UPI."
              enabled={!!demurrage.enabled}
              onToggle={(checked) => setDemurrage((prev) => ({ ...prev, enabled: checked }))}
              isOpen={openSection === 'demurrage'}
              onToggleOpen={() => toggleSection('demurrage')}
            >
              <div className={`space-y-3 ${demurrage.enabled ? '' : 'opacity-50 pointer-events-none'}`}>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className={formLabelClass}>Free hours</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={demurrage.free_hours}
                      onChange={(e) => setDemurrage((prev) => ({ ...prev, free_hours: e.target.value }))}
                      className={formInputClass}
                    />
                  </div>
                  <SelectField
                    label="Clock starts from"
                    placeholder="Select when the clock starts"
                    value={demurrage.clock_from}
                    portal
                    onChange={(e) => setDemurrage((prev) => ({ ...prev, clock_from: e.target.value }))}
                    options={[
                      { value: 'received', label: 'Received' },
                      { value: 'booked', label: 'Booked' },
                    ]}
                  />
                </div>
                <p className="text-[11px] text-gray-400">Slabs are fixed rupees. Leave max hours blank for the last (open-ended) slab.</p>
                {(demurrage.slabs || []).map((slab, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      placeholder="Max hrs"
                      value={slab.max_hours}
                      onChange={(e) => {
                        const slabs = [...demurrage.slabs];
                        slabs[idx] = { ...slabs[idx], max_hours: e.target.value };
                        setDemurrage((prev) => ({ ...prev, slabs }));
                      }}
                      className={`${formInputClass} w-24`}
                    />
                    <span className="text-xs text-gray-500">hrs → ₹</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={slab.amount}
                      onChange={(e) => {
                        const slabs = [...demurrage.slabs];
                        slabs[idx] = { ...slabs[idx], amount: e.target.value };
                        setDemurrage((prev) => ({ ...prev, slabs }));
                      }}
                      className={`${formInputClass} w-28`}
                    />
                    <button
                      type="button"
                      className="text-xs text-red-500"
                      onClick={() => setDemurrage((prev) => ({
                        ...prev,
                        slabs: prev.slabs.filter((_, i) => i !== idx),
                      }))}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="text-xs font-medium text-blue-600"
                  onClick={() => setDemurrage((prev) => ({
                    ...prev,
                    slabs: [...(prev.slabs || []), { max_hours: '', amount: 0 }],
                  }))}
                >
                  Add slab
                </button>
              </div>
            </SettingCard>
          )}

          {/* Commission remittance is a single rule (loading, unloading, and commission).
              The card stays hidden while there is nothing to choose. */}
          {/*
          {isInternalCompany && (
            <SettingCard
              icon={IndianRupee}
              iconTone="bg-violet-100 text-violet-700"
              title="Commission remittance"
              description="Loading, unloading, and commission stay on the agent cashbook. Commission % always uses total freight."
              enabled
              hideToggle
              isOpen={openSection === 'commissionRemit'}
              onToggleOpen={() => toggleSection('commissionRemit')}
            >
              <div className="space-y-2">
                {!(user?.company_features?.commission || user?.company_can_access?.commission) && (
                  <p className="text-[11px] text-amber-700 leading-relaxed">
                    Shipment Commission is off for this company. This rule is saved, and it applies once that feature is turned on.
                  </p>
                )}
                <div className="w-full text-left rounded-xl border border-violet-500 bg-violet-50 ring-1 ring-violet-400 p-3">
                  <p className="text-sm font-semibold text-gray-800">Keep loading, unloading, and commission</p>
                  <p className="text-[11px] text-gray-500 leading-relaxed mt-1">
                    The delivery agent keeps his commission and unloading. Those stay unpaid until Book &amp; Delivery Comm. On dispatch the booking agent cashbook gets the loading and the booking commission. On delivery the delivery agent cashbook gets the unloading and the delivery commission.
                  </p>
                </div>
              </div>
            </SettingCard>
          )}
          */}
            </>
          )}

          {currentCategory === 'booking' && (
            <>
          <SettingCard
            icon={Truck}
            iconTone="bg-orange-100 text-orange-600"
            title="Cargo Shipment Mode"
            description="Off = Bus booking (default). On = Cargo/pallet booking with package dimensions, CBM, and volumetric weight."
            enabled={!!isCargoShipment}
            onToggle={handleCargoToggle}
            isOpen={openSection === 'cargo'}
            onToggleOpen={() => toggleSection('cargo')}
          >
            <p className="text-[11px] text-gray-400 leading-relaxed">
              Applies to new bookings only. Existing shipments keep their original type. Enabling Cargo hides Bus Freight by Weight.
            </p>
          </SettingCard>

          <SettingCard
            icon={Layers}
            iconTone="bg-amber-100 text-amber-800"
            title="Loading & unloading"
            description="Pick one way of working. Click Save Settings after you choose."
            enabled
            hideToggle
            isOpen={openSection === 'labourPolicy'}
            onToggleOpen={() => toggleSection('labourPolicy')}
          >
            <LabourPolicyEditor value={labourPolicy} onChange={setLabourPolicy} />
          </SettingCard>

          {!isCargoShipment && (
            <SettingCard
              icon={Scale}
              iconTone="bg-sky-100 text-sky-600"
              title="Bus Freight by Weight"
              description="Off (default) = freight from qty × article rate. On = freight from Rate/KG × weight (article rate field becomes weight)."
              enabled={!!busFreightByWeight}
              onToggle={handleBusFreightToggle}
              isOpen={openSection === 'busFreight'}
              onToggleOpen={() => toggleSection('busFreight')}
            >
              <p className="text-[11px] text-gray-400 leading-relaxed">
                Only for normal (bus) bookings. Turning this on disables Cargo Shipment Mode. Enable Rate / KG under Shipment Charges to set the default rate.
              </p>
            </SettingCard>
          )}

          {isInternalCompany && (
            <SettingCard
              icon={Shuffle}
              iconTone="bg-indigo-100 text-indigo-600"
              title="Shipment Crossing"
              description="When enabled, Crossing appears in the sidebar and in Scan QR. Internal companies only."
              enabled={!!isCrossingEnabled}
              onToggle={setIsCrossingEnabled}
              isOpen={openSection === 'crossing'}
              onToggleOpen={() => toggleSection('crossing')}
            >
              <p className="text-[11px] text-gray-400 leading-relaxed">
                Off hides Crossing from the sidebar and the Scan QR Crossing tab, and blocks crossing APIs.
              </p>
            </SettingCard>
          )}
            </>
          )}

          {currentCategory === 'manifest' && (
            <>
          {isInternalCompany && (
            <SettingCard
              icon={Layers}
              iconTone="bg-emerald-100 text-emerald-600"
              title="Auto ML Creation"
              description="When enabled, Add Shipment shows the Auto ML Creation block to create or attach an ML sheet automatically."
              enabled={!!isAutoMlEnabled}
              onToggle={setIsAutoMlEnabled}
              isOpen={openSection === 'autoMl'}
              onToggleOpen={() => toggleSection('autoMl')}
            >
              <p className="text-[11px] text-gray-400 leading-relaxed">
                Off hides Auto ML Creation on Add Shipment. Internal companies only.
              </p>
            </SettingCard>
          )}

            <SettingCard
              icon={Wallet}
              iconTone="bg-amber-100 text-amber-700"
              title="Require Paid Settlement Before ML"
              description="When ON, agents must settle Paid collections (Paid Amount Settlement) before they can create an ML sheet."
              enabled={!!requirePaidSettlementBeforeMl}
              onToggle={setRequirePaidSettlementBeforeMl}
              isOpen={openSection === 'paidSettleBeforeMl'}
              onToggleOpen={() => toggleSection('paidSettleBeforeMl')}
            >
              <p className="text-[11px] text-gray-400 leading-relaxed">
                OFF = agents can create ML even with unremitted Paid cash. Paid Amount Settlement page stays available.
              </p>
            </SettingCard>

            <SettingCard
              icon={Lock}
              iconTone="bg-rose-100 text-rose-600"
              title="Lock ML Shipment Details Until Remit"
              description="When ON, agents cannot see Included Shipments on an ML sheet until they remit that sheet’s To-Pay via Return to Company."
              enabled={!!lockMlShipmentDetailsUntilRemit}
              onToggle={setLockMlShipmentDetailsUntilRemit}
              isOpen={openSection === 'lockMlDetails'}
              onToggleOpen={() => toggleSection('lockMlDetails')}
            >
              <p className="text-[11px] text-gray-400 leading-relaxed">
                OFF = agents always see shipment details on View ML, even if To-Pay is still outstanding.
              </p>
            </SettingCard>

            {isInternalCompany && (
            <SettingCard
              icon={Wallet}
              iconTone="bg-emerald-100 text-emerald-600"
              title="Own-hub settle collected cash only"
              description="Own branches remit cash actually collected (delivered To-Pay + extras), not the full ML face value."
              enabled={!!ownHubSettleCollectedOnly}
              onToggle={setOwnHubSettleCollectedOnly}
              isOpen={openSection === 'ownHubSettle'}
              onToggleOpen={() => toggleSection('ownHubSettle')}
            >
              <p className="text-[11px] text-gray-400 leading-relaxed">
                Loading and unloading on own hubs are included in the amount due to company. Agent-owned hubs keep the previous settlement rules.
              </p>
            </SettingCard>
            )}
            <SettingCard
              icon={Wallet}
              iconTone="bg-sky-100 text-sky-700"
              title="Labour reserve (cashbook)"
              description="When ON, each hub pays an advance % of loading/unloading to cashbook on ML create and holds the remainder as pending reserve."
              enabled={!!labourReserveEnabled}
              onToggle={setLabourReserveEnabled}
              isOpen={openSection === 'labourReserve'}
              onToggleOpen={() => toggleSection('labourReserve')}
            >
              <p className="text-[11px] text-gray-400 leading-relaxed">
                Set Loading reserve % (booking agent) and Unloading reserve % (delivery agent) on each Location Hub. Cash portion posts now; reserve stays pending until later settle. Does not change GST, commission, or To-Pay.
              </p>
            </SettingCard>
            {isInternalCompany && (
            <SettingCard
              icon={Wallet}
              iconTone="bg-emerald-100 text-emerald-700"
              title="Branch cashbook transfer"
              description="Tick one or more From branches and one or more admin branches. Each From cashbook transfers its balance to one of those admin branches."
              enabled
              onToggle={() => {}}
              hideToggle
              isOpen={openSection === 'settlementDest'}
              onToggleOpen={() => toggleSection('settlementDest')}
            >
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    From branch (collects settlement)
                  </label>
                  <DestinationHubMultiSelect
                    hubs={settleHubOptions.filter((hub) => !adminTransferHubIds.includes(String(hub.id)))}
                    value={collectHubIds}
                    onChange={setCollectHubIds}
                    placeholder="Tick one or more branches"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    To branch (admin hub)
                  </label>
                  <DestinationHubMultiSelect
                    hubs={settleHubOptions.filter((hub) => !collectHubIds.includes(String(hub.id)))}
                    value={adminTransferHubIds}
                    onChange={setAdminTransferHubIds}
                    placeholder="Tick one or more admin branches"
                  />
                </div>
                <p className="text-[11px] text-gray-400 leading-relaxed">
                  Tick one From branch, or several. Transfer appears only on those cashbooks. With one admin branch the balance goes there. With several, pick the admin branch on the cashbook. Agents still settle only to the hubs ticked on their own location hub.
                </p>
              </div>
            </SettingCard>
            )}
            </>
          )}

          {currentCategory === 'documents' && (
            <>
          <SettingCard
            icon={FileSpreadsheet}
            iconTone="bg-cyan-100 text-cyan-700"
            title="ML Sheet Format"
            description="Page orientation, summary style, and which shipment charges print on the financial PDF."
            hideToggle
            isOpen={openSection === 'mlSheetPdf'}
            onToggleOpen={() => toggleSection('mlSheetPdf')}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 min-w-0">
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-700">Orientation</p>
                {[
                  ['landscape', 'Landscape'],
                  ['portrait', 'Portrait'],
                ].map(([id, label]) => (
                  <label key={id} className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="ml_sheet_pdf_orientation"
                      checked={mlSheetPdfOrientation === id}
                      onChange={() => setMlSheetPdfOrientation(id)}
                      className="w-4 h-4 mt-0.5 text-buttonBg focus:ring-blue-400"
                    />
                    <span className="text-xs sm:text-sm font-medium text-secondary">{label}</span>
                  </label>
                ))}
              </div>
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-700">Summary</p>
                {[
                  ['financial', 'Financial (show amounts)'],
                  ['operational', 'Operational (hide rupees)'],
                ].map(([id, label]) => (
                  <label key={id} className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="ml_sheet_summary_mode"
                      checked={mlSheetSummaryMode === id}
                      onChange={() => setMlSheetSummaryMode(id)}
                      className="w-4 h-4 mt-0.5 text-buttonBg focus:ring-blue-400"
                    />
                    <span className="text-xs sm:text-sm font-medium text-secondary">{label}</span>
                  </label>
                ))}
              </div>
            </div>
            <p className="text-[11px] text-gray-400 leading-relaxed mt-3">
              Operational mode hides rupee columns. Landscape stays the default. Long names wrap inside the column when many columns are on.
            </p>
            <div className="mt-4 pt-3 border-t border-slate-100 space-y-3">
              <div>
                <p className="text-xs font-semibold text-gray-700">Shipment columns</p>
                <p className="text-[11px] text-gray-400 leading-relaxed mt-0.5">
                  These are the columns on the shipment table. Turn one off to leave it out of the PDF.
                </p>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2">
                  {ML_TABLE_COLUMNS.filter((col) => mlSheetSummaryMode === 'financial' || !col.amount).map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-2 min-w-0">
                      <Toggle
                        checked={!!mlSheetPdfCharges[key]}
                        onChange={(checked) => {
                          setMlSheetPdfCharges((prev) => ({ ...prev, [key]: checked }));
                        }}
                      />
                      <span className="text-xs sm:text-sm font-medium text-secondary truncate">{label}</span>
                    </div>
                  ))}
                </div>
              </div>
              {mlSheetSummaryMode === 'financial' && (
                <div>
                  <p className="text-xs font-semibold text-gray-700">Summary columns</p>
                  <p className="text-[11px] text-gray-400 leading-relaxed mt-0.5">
                    Charges turned on in Shipment Charges start enabled. Freight and ST start on. Turn a column off to leave it out of the summary.
                  </p>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2">
                    {ML_SUMMARY_COLUMNS.map(({ key, label }) => (
                      <div key={key} className="flex items-center gap-2 min-w-0">
                        <Toggle
                          checked={!!mlSheetPdfCharges[key]}
                          onChange={(checked) => {
                            setMlSheetPdfCharges((prev) => ({ ...prev, [key]: checked }));
                          }}
                        />
                        <span className="text-xs sm:text-sm font-medium text-secondary truncate">{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </SettingCard>

          <SettingCard
            icon={FileDigit}
            iconTone="bg-violet-100 text-violet-700"
            title="Shipment & ML ID format"
            description="Choose how new shipment numbers are built. Existing numbers stay as they are."
            enabled
            onToggle={() => {}}
            isOpen={openSection === 'idFormat'}
            onToggleOpen={() => toggleSection('idFormat')}
            hideToggle
          >
            <div className="space-y-5">
              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-700">Shipment / GC ID</p>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="shipment_id_style"
                    checked={shipmentIdFormat !== LR_SHIPMENT_ID_FORMAT}
                    onChange={() => {
                      if (shipmentIdFormat === LR_SHIPMENT_ID_FORMAT) {
                        setShipmentIdFormat(DEFAULT_SHIPMENT_ID_FORMAT)
                      }
                    }}
                    className="w-4 h-4 mt-0.5 text-buttonBg focus:ring-blue-400"
                  />
                  <span className="text-xs sm:text-sm text-secondary">
                    Existing location code. Keep the hub code you already use, such as MU or CMRJP.
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="shipment_id_style"
                    checked={shipmentIdFormat === LR_SHIPMENT_ID_FORMAT}
                    onChange={() => {
                      setShipmentIdFormat(LR_SHIPMENT_ID_FORMAT)
                      setLrBranchCodeRequired(true)
                    }}
                    className="w-4 h-4 mt-0.5 text-buttonBg focus:ring-blue-400"
                  />
                  <span className="text-xs sm:text-sm text-secondary">
                    Number sequence. Example 05AC-7-1001. Branch 05, year 2025 (A), March (C), user id 7, serial 1001.
                  </span>
                </label>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold text-gray-800">LR branch code on location hubs</p>
                  <p className="text-[11px] text-gray-500 leading-relaxed mt-0.5">
                    When ON, each Location Hub shows a 2-digit code (00-99) and it is required to save.
                    Number sequence GC IDs use this code.
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!lrBranchCodeRequired}
                  onClick={() => setLrBranchCodeRequired((v) => !v)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                    lrBranchCodeRequired ? 'bg-buttonBg' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                      lrBranchCodeRequired ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
              {shipmentIdFormat === LR_SHIPMENT_ID_FORMAT ? (
                <p className="text-[11px] text-gray-500 leading-relaxed">
                  The 2-digit code field appears on each location hub. Set one code (00-99) per branch.
                  The user code is that user’s primary id. The serial counts up for that branch in that month and starts again next month.
                  With a location sequence, Start again each month uses that same monthly serial. Leave it off to keep one running serial.
                  The number is created when the booking is saved and cannot be typed.
                </p>
              ) : (
                <DocumentIdFormatBuilder
                  label="Shipment / GC ID"
                  value={shipmentIdFormat}
                  onChange={setShipmentIdFormat}
                />
              )}
              <DocumentIdFormatBuilder
                label="ML sheet ID"
                value={mlSheetIdFormat}
                onChange={setMlSheetIdFormat}
              />
              <p className="text-[11px] text-gray-400 leading-relaxed">
                Number ({'{seq}'}) is required and counts up. Click a separator (- / . _) to change it.
                With one sequence for the whole company, the count starts again when the sender hub, receiver hub, or year in the pattern changes.
                A location sequence keeps one count for the from hub. Turn on Start again each month when that count should return to 1 on the 1st. Month letter is A for January through L for December.
              </p>
              <HubSequenceSettings
                mode={sequenceMode}
                onModeChange={setSequenceMode}
                starts={sequenceStarts}
                onStartChange={(hubId, field, value) => {
                  setSequenceStarts((prev) => prev.map((row) => (
                    row.location_hub_id === hubId ? { ...row, [field]: value } : row
                  )));
                }}
                shipmentFormat={shipmentIdFormat}
                mlFormat={mlSheetIdFormat}
                resetsMonthly={sequenceResetsMonthly}
                onResetsMonthlyChange={(next) => {
                  setSequenceResetsMonthly(next);
                  if (next) {
                    setShipmentIdFormat((fmt) => ensureMonthLetter(fmt || DEFAULT_SHIPMENT_ID_FORMAT));
                    setMlSheetIdFormat((fmt) => ensureMonthLetter(fmt || DEFAULT_ML_SHEET_ID_FORMAT));
                  }
                }}
              />
            </div>
          </SettingCard>
            </>
          )}

          {currentCategory === 'booking' && (
            <>
          <SettingCard
            icon={Layers}
            iconTone="bg-orange-100 text-orange-600"
            title="Parcel types"
            description="Types shown on Add Shipment. GST-exempt types skip GST. Only Admin can edit; booking can add new names."
            hideToggle
            isOpen={openSection === 'parcelTypes'}
            onToggleOpen={() => toggleSection('parcelTypes')}
          >
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  value={parcelDraft}
                  onChange={(e) => setParcelDraft(e.target.value.toUpperCase())}
                  placeholder="NEW TYPE"
                  className={`${formInputClass} flex-1 uppercase`}
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={async () => {
                    const name = parcelDraft.trim().toUpperCase();
                    if (!name) return;
                    try {
                      const res = await companySettingsService.createParcelType({ name });
                      setParcelTypes((prev) => {
                        const row = res?.data;
                        if (!row) return prev;
                        if (prev.some((p) => p.id === row.id)) return prev;
                        return [...prev, row];
                      });
                      setParcelDraft('');
                    } catch (err) {
                      Alert.error(err.response?.data?.error || 'Failed to add parcel type');
                    }
                  }}
                >
                  Add
                </Button>
              </div>
              <div className="max-h-56 overflow-y-auto border border-gray-100 rounded-lg divide-y">
                {parcelTypes.map((row) => (
                  <div key={row.id} className={`flex items-center gap-2 px-2 py-1.5 text-sm ${row.is_active ? '' : 'opacity-50'}`}>
                    <span className="flex-1 font-medium text-gray-800">{row.name}</span>
                    <label className="flex items-center gap-1 text-xs text-gray-500">
                      <input
                        type="checkbox"
                        checked={!!row.gst_exempt}
                        onChange={async (e) => {
                          try {
                            const res = await companySettingsService.updateParcelType(row.id, { gst_exempt: e.target.checked });
                            setParcelTypes((prev) => prev.map((p) => (p.id === row.id ? res.data : p)));
                          } catch (err) {
                            Alert.error(err.response?.data?.error || 'Failed to update');
                          }
                        }}
                      />
                      GST exempt
                    </label>
                    <button
                      type="button"
                      className="text-xs text-red-500"
                      onClick={async () => {
                        try {
                          const res = await companySettingsService.archiveParcelType(row.id);
                          setParcelTypes((prev) => prev.map((p) => (p.id === row.id ? res.data : p)));
                        } catch (err) {
                          Alert.error(err.response?.data?.error || 'Failed to archive');
                        }
                      }}
                    >
                      Hide
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </SettingCard>
            </>
          )}

          {currentCategory === 'hr' && attendanceEnabled && (
            <SettingCard
              icon={Calendar}
              iconTone="bg-blue-100 text-blue-600"
              title="Salary Cycle"
              description="Attendance for a selected salary month is counted from this day of the previous month up to the day before this day of the selected month. Day 1 uses the calendar month."
              hideToggle
              isOpen={openSection === 'salaryCycle'}
              onToggleOpen={() => toggleSection('salaryCycle')}
            >
              <div className="space-y-2 min-w-0">
                <SelectField
                  id="salary_cycle_start_day"
                  label="Cycle start day"
                  placeholder="Select the cycle start day"
                  value={String(salaryCycleStartDay)}
                  portal
                  onChange={(e) => setSalaryCycleStartDay(parseInt(e.target.value, 10) || 1)}
                  options={Array.from({ length: 28 }, (_, i) => i + 1).map((day) => ({
                    value: String(day),
                    label: day === 1
                      ? '1 — Calendar month (1st to last day)'
                      : `${day} — ${ordinalDay(day)} to ${ordinalDay(day)}`,
                  }))}
                />
                <p className="text-[11px] text-gray-400 leading-relaxed">
                  {salaryCycleStartDay <= 1
                    ? 'July salary uses 1 Jul through 31 Jul.'
                    : `Example: July salary uses the ${ordinalDay(salaryCycleStartDay)} of June through the ${ordinalDay(salaryCycleStartDay - 1)} of July.`}
                </p>
              </div>
            </SettingCard>
          )}

          {currentCategory === 'finance' && (
          <SettingCard
            icon={IndianRupee}
            iconTone="bg-emerald-100 text-emerald-600"
            title="Shipment Charges"
            description="Shared default charges for bus and cargo. Enable each charge to show it on the shipment page. Extra charges can be a fixed ₹ or a % of article charge (remaining % is freight). Total Amount is calculated. Insurance % is of remainder freight. Rate / KG is set per Location Hub (receiver)."
            enabled={Object.values(charges).some((c) => c.enabled)}
            onToggle={(checked) => {
              setCharges((prev) => {
                const next = { ...prev };
                Object.keys(next).forEach((key) => {
                  next[key] = { ...next[key], enabled: checked };
                });
                return next;
              });
              if (checked) {
                setMlSheetPdfCharges((prev) => {
                  const next = { ...prev };
                  CHARGE_FIELDS.forEach(({ key }) => {
                    next[key] = true;
                  });
                  return next;
                });
              }
            }}
            isOpen={openSection === 'charges'}
            onToggleOpen={() => toggleSection('charges')}
          >
            <div className="space-y-2 min-w-0">
              {CHARGE_FIELDS.map(({ key, label }) => {
                const chargeMode = charges[key]?.mode === 'percent' ? 'percent' : 'fixed';
                const displayLabel = chargeMode === 'percent' ? `${label} (%)` : `${label} (₹)`;
                return (
                <div
                  key={key}
                  className={`flex items-center gap-1.5 sm:gap-2 min-w-0 w-full ${
                    charges[key]?.enabled ? '' : 'opacity-50'
                  }`}
                >
                  <Toggle
                    checked={!!charges[key]?.enabled}
                    onChange={(checked) => handleChargeChange(key, 'enabled', checked)}
                  />
                  <span className="text-xs sm:text-sm font-medium text-secondary flex-1 min-w-0 truncate">
                    {displayLabel}
                  </span>
                  <div className="flex shrink-0 rounded-lg border border-gray-300 overflow-hidden text-[10px] sm:text-xs font-medium">
                    <button
                      type="button"
                      disabled={!charges[key]?.enabled}
                      onClick={() => handleChargeChange(key, 'mode', 'fixed')}
                      className={`px-1.5 sm:px-2 py-1 ${
                        chargeMode === 'fixed'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      } disabled:cursor-not-allowed`}
                    >
                      Fixed
                    </button>
                    <button
                      type="button"
                      disabled={!charges[key]?.enabled}
                      onClick={() => handleChargeChange(key, 'mode', 'percent')}
                      className={`px-1.5 sm:px-2 py-1 border-l border-gray-300 ${
                        chargeMode === 'percent'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      } disabled:cursor-not-allowed`}
                    >
                      %
                    </button>
                  </div>
                  <input
                    type="number"
                    min="0"
                    max={chargeMode === 'percent' ? '100' : undefined}
                    step="0.01"
                    value={charges[key]?.amount ?? 0}
                    onChange={(e) => handleChargeChange(key, 'amount', e.target.value)}
                    disabled={!charges[key]?.enabled}
                    className="w-14 sm:w-20 md:w-24 shrink-0 px-2 py-1.5 sm:px-3 sm:py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                  />
                </div>
                );
              })}
              {(() => {
                const extraPercentSum = sumEnabledPercentCharges(charges, labourPolicy);
                if (extraPercentSum <= 0) return null;
                return (
                  <p className={`text-[11px] leading-relaxed ${extraPercentSum > 100 ? 'text-red-600' : 'text-gray-500'}`}>
                    Extra charges in % mode: {extraPercentSum}% of article charge · remaining {Math.max(0, 100 - extraPercentSum)}% is freight
                    {extraPercentSum > 100 ? ' (must be 100% or less)' : ''}
                  </p>
                );
              })()}
            </div>
          </SettingCard>
          )}

          {currentCategory === 'documents' && (
          <SettingCard
            icon={FileSpreadsheet}
            iconTone="bg-violet-100 text-violet-600"
            title="Shipment Export Columns"
            description="Choose columns after Route for the shipment list PDF and the shipment report Excel. SR through Route are always included."
            enabled={OPTIONAL_EXPORT_FIELDS.some((f) => exportColumns[f.key])}
            onToggle={(checked) => {
              setExportColumns((prev) => {
                const next = { ...prev };
                OPTIONAL_EXPORT_FIELDS.forEach(({ key }) => {
                  next[key] = checked;
                });
                if (rcm.enabled) {
                  RCM_EXPORT_FIELDS.forEach(({ key }) => {
                    next[key] = true;
                  });
                }
                return next;
              });
            }}
            isOpen={openSection === 'exportColumns'}
            onToggleOpen={() => toggleSection('exportColumns')}
          >
            <div className="space-y-2 min-w-0 max-h-72 overflow-y-auto pr-1">
              <p className="text-[11px] text-gray-400 leading-relaxed mb-1">
                Always: SR, Shipment ID, Date, Sender, Receiver, Route
              </p>
              {OPTIONAL_EXPORT_FIELDS.map(({ key, label }) => (
                <label
                  key={key}
                  className="flex items-center gap-2 min-w-0 w-full cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={!!exportColumns[key]}
                    onChange={(e) => handleExportColumnChange(key, e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-buttonBg focus:ring-blue-400"
                  />
                  <span className="text-xs sm:text-sm font-medium text-secondary">
                    {label}
                  </span>
                </label>
              ))}
              {rcm.enabled && (
                <>
                  <p className="text-[11px] text-gray-400 leading-relaxed pt-1">
                    Required when RCM is enabled
                  </p>
                  {RCM_EXPORT_FIELDS.map(({ key, label }) => (
                    <label
                      key={key}
                      className="flex items-center gap-2 min-w-0 w-full opacity-80 cursor-not-allowed"
                    >
                      <input
                        type="checkbox"
                        checked
                        disabled
                        className="w-4 h-4 rounded border-gray-300 text-buttonBg"
                      />
                      <span className="text-xs sm:text-sm font-medium text-secondary">
                        {label}
                      </span>
                    </label>
                  ))}
                </>
              )}
            </div>
          </SettingCard>
          )}

          {currentCategory === 'documents' && (
          <SettingCard
            icon={Users}
            iconTone="bg-indigo-100 text-indigo-700"
            title="Role Module Access"
            description="Choose a role. Every sidebar item that role already has is selected. Uncheck one to hide it. Admin always keeps the full sidebar."
            hideToggle
            isOpen={openSection === 'roleModules'}
            onToggleOpen={() => toggleSection('roleModules')}
          >
            <RoleModuleAccessFields
              role={roleModuleRole}
              onRoleChange={setRoleModuleRole}
              access={roleModuleAccess}
              onToggle={(moduleKey, checked) => {
                setRoleModuleAccess((prev) => ({
                  ...prev,
                  [roleModuleRole]: {
                    ...(prev[roleModuleRole] || {}),
                    [moduleKey]: checked,
                  },
                }));
              }}
            />
          </SettingCard>
          )}

          {currentCategory === 'documents' && (
          <SettingCard
            icon={ShieldCheck}
            iconTone="bg-sky-100 text-sky-700"
            title="Report Permissions"
            description="Pick a user, then choose which reports that person can open. Admin and System Admin always keep every report."
            hideToggle
            isOpen={openSection === 'reportPermissions'}
            onToggleOpen={() => toggleSection('reportPermissions')}
          >
            {companyUsers.length === 0 ? (
              <p className="text-[11px] text-gray-400">No company users to configure.</p>
            ) : (
            <div className="space-y-3 min-w-0">
              <div className="flex flex-wrap items-center gap-2 min-w-0">
                <div className="flex-1 min-w-[16rem]">
                  <SelectField
                    value={reportPermUserId}
                    onChange={(e) => setReportPermUserId(e.target.value)}
                    placeholder="Select a user"
                    portal
                    className="w-full"
                    options={companyUsers.map((u) => ({
                      value: u.user_id,
                      label: reportUserOptionContent(u),
                    }))}
                  />
                </div>
                {!selectedReportLocked && (
                  <>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      icon={CheckSquare}
                      title="Select all"
                      aria-label="Select all"
                      onClick={() => toggleAllReportsForUser(true)}
                    >
                      Select all
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      icon={Square}
                      title="Clear"
                      aria-label="Clear"
                      onClick={() => toggleAllReportsForUser(false)}
                    >
                      Clear
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      icon={RotateCcw}
                      title="Role defaults"
                      aria-label="Role defaults"
                      onClick={resetReportUserToDefaults}
                    >
                      Role defaults
                    </Button>
                  </>
                )}
                <span className={`text-[10px] font-semibold px-2 py-1 rounded-full border whitespace-nowrap ${
                  selectedReportLocked
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : customizedReportUsers.has(String(reportPermUserId))
                      ? 'bg-blue-50 text-blue-700 border-blue-200'
                      : 'bg-gray-50 text-gray-600 border-gray-200'
                }`}
                >
                  {selectedReportLocked
                    ? 'Full access'
                    : customizedReportUsers.has(String(reportPermUserId))
                      ? 'Custom'
                      : 'Defaults'}
                </span>
              </div>
              <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                <div className="hidden sm:grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500 font-semibold border-b border-gray-200">
                  <div className="col-span-1">Allow</div>
                  <div className="col-span-4">Report name</div>
                  <div className="col-span-3">Category</div>
                  <div className="col-span-4">Description</div>
                </div>
                <div className="divide-y divide-gray-100 max-h-[17.5rem] overflow-y-scroll overscroll-contain">
                  {REPORT_TYPE_OPTIONS.map(({ type, title, description, categoryTitle }) => {
                    const checked = selectedReportTypes.includes(type);
                    return (
                      <label
                        key={type}
                        className={`grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-2 px-3 py-2.5 items-start sm:items-center min-w-0 ${
                          selectedReportLocked ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'
                        }`}
                      >
                        <div className="sm:col-span-1 flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={selectedReportLocked || !reportPermUserId}
                            onChange={(e) => toggleReportTypeForUser(type, e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 text-buttonBg focus:ring-blue-400"
                          />
                          <span className="sm:hidden text-xs font-semibold text-secondary">{title}</span>
                        </div>
                        <div className="sm:col-span-4 min-w-0 hidden sm:block">
                          <p className="text-xs sm:text-sm font-semibold text-secondary truncate">{title}</p>
                          <p className="text-[10px] text-gray-400 font-mono truncate">{type}</p>
                        </div>
                        <div className="sm:col-span-3 min-w-0">
                          <span className="inline-flex text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100">
                            {categoryTitle}
                          </span>
                          <p className="sm:hidden text-[11px] text-gray-500 mt-1 leading-snug">{description}</p>
                        </div>
                        <div className="sm:col-span-4 min-w-0 hidden sm:block">
                          <p className="text-[11px] sm:text-xs text-gray-500 leading-snug line-clamp-2">{description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50">
                  <span className="text-[11px] text-gray-500">
                    {REPORT_TYPE_OPTIONS.length} reports · first 5 shown · scroll for more
                  </span>
                </div>
              </div>
            </div>
            )}
          </SettingCard>
          )}
        </div>
        </SettingsModalContext.Provider>
      </div>
    </DashboardLayout>
  );
};

const LABOUR_FLOW_STEPS = [
  { id: 'booking', label: 'Booking' },
  { id: 'dispatch', label: 'Dispatch' },
  { id: 'received', label: 'Received' },
  { id: 'delivery', label: 'Delivered' },
];

function LabourPolicyEditor({ value, onChange }) {
  const policy = normalizeLabourChargePolicy(value);
  const workflow = labourWorkflowId(policy);
  const [showCustom, setShowCustom] = useState(workflow === 'custom');

  const applyWorkflow = (item) => {
    onChange({
      loading: { capture: item.loading },
      unloading: { capture: item.unloading },
    });
    setShowCustom(false);
  };

  const setCapture = (kind, capture) => {
    onChange({
      ...policy,
      [kind]: { capture },
    });
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {LABOUR_WORKFLOWS.map((item) => {
          const selected = workflow === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => applyWorkflow(item)}
              className={`text-left rounded-xl border p-3 transition-colors ${
                selected
                  ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-400'
                  : 'border-gray-200 bg-white hover:border-amber-300'
              }`}
            >
              <p className="text-sm font-semibold text-gray-800">{item.title}</p>
              <p className="text-[11px] text-gray-500 leading-relaxed mt-1">{item.hint}</p>
            </button>
          );
        })}
      </div>

      <div className="rounded-xl border border-gray-100 bg-gray-50 px-2 py-2.5">
        <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-2 px-1">When it goes on the GC</p>
        <div className="grid grid-cols-4 gap-1">
          {LABOUR_FLOW_STEPS.map((step) => {
            const loadingHere = policy.loading.capture === step.id;
            const unloadingHere = policy.unloading.capture === step.id;
            return (
              <div key={step.id} className="rounded-lg bg-white border border-gray-100 px-1.5 py-2 text-center min-w-0">
                <p className="text-[10px] font-medium text-gray-500 truncate">{step.label}</p>
                <div className="mt-1 space-y-0.5 min-h-[2.25rem]">
                  {loadingHere ? (
                    <p className="text-[10px] font-semibold text-amber-800">Loading</p>
                  ) : null}
                  {unloadingHere ? (
                    <p className="text-[10px] font-semibold text-sky-800">Unloading</p>
                  ) : null}
                  {!loadingHere && !unloadingHere ? (
                    <p className="text-[10px] text-gray-300">—</p>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
        <div className="rounded-lg border border-gray-100 px-3 py-2">
          <p className="font-semibold text-gray-700">Who keeps loading</p>
          <p className="text-gray-500 mt-0.5">Booking agent (origin AGENT hub)</p>
        </div>
        <div className="rounded-lg border border-gray-100 px-3 py-2">
          <p className="font-semibold text-gray-700">Who keeps unloading</p>
          <p className="text-gray-500 mt-0.5">Receiving agent (dest AGENT hub)</p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setShowCustom((open) => !open)}
        className="text-[11px] text-gray-500 hover:text-gray-800"
      >
        {showCustom ? 'Hide other timings' : 'Need a different timing?'}
      </button>

      {showCustom ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            ['loading', 'Loading'],
            ['unloading', 'Unloading'],
          ].map(([kind, label]) => (
            <div key={kind}>
              <SelectField
                label={label}
                placeholder={`Select when to capture ${label.toLowerCase()}`}
                value={policy[kind]?.capture || ''}
                portal
                onChange={(e) => setCapture(kind, e.target.value)}
                options={LABOUR_CAPTURE_OPTIONS}
              />
            </div>
          ))}
        </div>
      ) : null}

      <p className="text-[11px] text-gray-400 leading-relaxed">
        Own hubs do not keep labour. Amounts come from Finance → Shipment Charges. Existing GCs stay as they are.
      </p>
    </div>
  );
}

function RoleModuleAccessFields({ role, onRoleChange, access, onToggle }) {
  const modules = access?.[role] || {};
  const keys = ROLE_MODULE_ORDER.filter((key) => Object.prototype.hasOwnProperty.call(modules, key));

  return (
    <div className="space-y-3 min-w-0">
      <div>
        <SelectField
          id="role_module_role"
          label="Role"
          placeholder="Select a role"
          value={role}
          portal
          onChange={(e) => onRoleChange(e.target.value)}
          options={CONFIGURABLE_ROLES.map((name) => ({ value: name, label: name }))}
        />
      </div>
      {keys.length === 0 ? (
        <p className="text-[11px] text-gray-400 leading-relaxed">
          No modules are turned on for this company, so there is nothing to assign for this role.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {keys.map((key) => (
            <label key={key} className="flex items-center gap-2 min-w-0 cursor-pointer">
              <input
                type="checkbox"
                checked={modules[key] !== false}
                onChange={(e) => onToggle(key, e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-buttonBg focus:ring-blue-400"
              />
              <span className="text-xs sm:text-sm font-medium text-secondary truncate">
                {ROLE_MODULE_LABELS[key] || key}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingCard({
  icon: Icon,
  iconTone,
  title,
  description,
  enabled,
  onToggle,
  toggleDisabled = false,
  hideToggle = false,
  isOpen,
  onToggleOpen,
  children,
}) {
  const { saving, onSave, onCancel } = useContext(SettingsModalContext);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;

  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape') cancelRef.current?.();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  const statusLabel = hideToggle ? null : (enabled ? 'On' : 'Off');

  return (
    <>
      <button
        type="button"
        data-settings-card
        onClick={onToggleOpen}
        className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-4 sm:p-5 flex flex-col gap-3 min-h-[9.5rem] min-w-0 h-full text-left transition hover:border-blue-200 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
      >
        <div className="flex items-start justify-between gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${iconTone}`}>
            <Icon className="w-5 h-5" />
          </div>
          {statusLabel && (
            <span
              className={`inline-flex items-center rounded-full px-3.5 py-1.5 text-sm font-bold uppercase tracking-wide ${
                enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
              }`}
            >
              {statusLabel}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm sm:text-base font-semibold text-secondary leading-snug">
            {title}
          </h2>
          {description ? (
            <p className="mt-1.5 text-xs sm:text-[13px] text-gray-500 leading-relaxed line-clamp-3">
              {description}
            </p>
          ) : null}
        </div>
      </button>
      {isOpen && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-[90] bg-slate-900/50 flex items-end sm:items-center justify-center p-0 sm:p-4 md:p-6"
          onClick={() => onCancel?.()}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={(event) => event.stopPropagation()}
            className="bg-white w-full h-[100dvh] sm:h-auto sm:max-h-[min(92dvh,52rem)] sm:max-w-3xl lg:max-w-5xl sm:rounded-2xl shadow-2xl border border-slate-200 flex flex-col min-w-0 overflow-hidden"
          >
            <div className="flex items-start gap-3 px-4 sm:px-6 py-4 border-b border-slate-100 shrink-0">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${iconTone}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base sm:text-lg font-semibold text-secondary leading-snug">{title}</h2>
                {description ? (
                  <p className="mt-1 text-xs sm:text-sm text-gray-500 leading-relaxed">{description}</p>
                ) : null}
              </div>
              {!hideToggle && (
                <div className={toggleDisabled ? 'opacity-50 shrink-0' : 'shrink-0'} title={toggleDisabled ? 'Disabled while Bus Freight by Weight is on' : undefined}>
                  <Toggle checked={enabled} onChange={onToggle} disabled={toggleDisabled} />
                </div>
              )}
              <button
                type="button"
                onClick={() => onCancel?.()}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-slate-100 hover:text-gray-800 shrink-0"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-6 py-4 sm:py-5">
              {children}
            </div>
            <div className="shrink-0 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-4 sm:px-6 py-3 border-t border-slate-100 bg-slate-50/80">
              <Button type="button" variant="secondary" onClick={() => onCancel?.()} disabled={saving}>
                Cancel
              </Button>
              <Button type="button" variant="primary" icon={Save} onClick={onSave} loading={saving}>
                Save
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function Toggle({ checked, onChange, disabled = false }) {
  return (
    <label
      className={`relative inline-flex items-center shrink-0 mt-0.5 ${
        disabled ? 'cursor-not-allowed' : 'cursor-pointer'
      }`}
    >
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        disabled={disabled}
        onChange={(e) => {
          if (disabled) return;
          onChange(e.target.checked);
        }}
      />
      <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-buttonBg peer-disabled:opacity-60" />
    </label>
  );
}

function RateInput({ label, value, onChange }) {
  return (
    <div className="min-w-0">
      <label className={formLabelClass}>{label}</label>
      <input
        type="number"
        min="0"
        step="0.01"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={formInputClass()}
      />
    </div>
  );
}

export default CompanySettings;
